import { useBreakpoint } from './chunk-FUVISKLY.js';
export { formatPageRangeSummary, formatPageSummary } from './chunk-FUVISKLY.js';
export { Brand } from './chunk-EHMYFYGQ.js';
import { defaultLinkComponent } from './chunk-46OQMWYR.js';
import { AdminerContext, useT } from './chunk-3G2YSMCN.js';
import { getFormattingConfig, formatNumber, formatCurrency, formatRelative, formatWeekday, formatTime, formatDateTime, formatDate } from './chunk-SUNYEKTQ.js';
import { dayjs } from './chunk-WH2C42V5.js';
import './chunk-RRFUPILC.js';
import './chunk-4DPTK2CR.js';
import clsx9 from 'clsx';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useId, useContext, Component, useState, useEffect, useCallback, useRef } from 'react';
import { DayPicker, rangeIncludesDate } from 'react-day-picker';
import { cs } from 'react-day-picker/locale';
import { X, CalendarDays, Clock } from 'lucide-react';
import { createPortal } from 'react-dom';

function Alert({ variant, title, children, className }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx9("adminer-Alert", `adminer-Alert--${variant}`, className),
      role: "alert",
      children: [
        title && /* @__PURE__ */ jsx("strong", { className: "adminer-Alert__title", children: title }),
        children && /* @__PURE__ */ jsx("span", { children })
      ]
    }
  );
}
function Badge({ variant = "muted", children, className }) {
  return /* @__PURE__ */ jsx("span", { className: clsx9("adminer-Badge", `adminer-Badge--${variant}`, className), children });
}
function classNames(variant, size, block, loading, extra) {
  const parts = [
    "adminer-Button",
    `adminer-Button--${variant}`,
    `adminer-Button--${size}`
  ];
  if (block) parts.push("adminer-Button--block");
  if (loading) parts.push("adminer-Button--loading");
  if (extra) parts.push(extra);
  return parts.join(" ");
}
function DefaultButton({
  variant = "default",
  size = "md",
  block = false,
  loading = false,
  startIcon,
  endIcon,
  disabled,
  children,
  className,
  type = "button",
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ...rest,
      type,
      disabled: disabled || loading,
      className: classNames(variant, size, block, loading, className),
      children: [
        loading ? /* @__PURE__ */ jsx("span", { className: "adminer-Button__spinner", "aria-hidden": "true" }) : startIcon && /* @__PURE__ */ jsx("span", { className: "adminer-Button__icon", children: startIcon }),
        children != null && /* @__PURE__ */ jsx("span", { className: "adminer-Button__label", children }),
        endIcon && !loading && /* @__PURE__ */ jsx("span", { className: "adminer-Button__icon", children: endIcon })
      ]
    }
  );
}
function Button(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.Button;
  const Component2 = Override ?? DefaultButton;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function isRenderable(node) {
  return node !== null && node !== void 0 && node !== false;
}
function CardHeader({
  title,
  subtitle,
  subtitlePlacement = "below",
  actions,
  className
}) {
  const hasSubtitle = isRenderable(subtitle);
  const hasRightSubtitle = hasSubtitle && subtitlePlacement === "right";
  const hasActions = isRenderable(actions);
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-CardHeader", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "adminer-CardHeader__titleBlock", children: [
      /* @__PURE__ */ jsx("h2", { className: "adminer-CardHeader__title", children: title }),
      hasSubtitle && subtitlePlacement === "below" && /* @__PURE__ */ jsx("div", { className: "adminer-CardHeader__subtitle", children: subtitle })
    ] }),
    (hasRightSubtitle || hasActions) && /* @__PURE__ */ jsxs("div", { className: "adminer-CardHeader__side", children: [
      hasRightSubtitle && /* @__PURE__ */ jsx("div", { className: "adminer-CardHeader__subtitle adminer-CardHeader__subtitle--right", children: subtitle }),
      hasActions && /* @__PURE__ */ jsx("div", { className: "adminer-CardHeader__actions", children: actions })
    ] })
  ] });
}
function CardRoot({
  header,
  footer,
  children,
  className,
  bodyPadding = "md"
}) {
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Card", className), children: [
    header && /* @__PURE__ */ jsx("div", { className: "adminer-Card__header", children: header }),
    /* @__PURE__ */ jsx("div", { className: clsx9("adminer-Card__body", `adminer-Card__body--${bodyPadding}`), children }),
    footer && /* @__PURE__ */ jsx("div", { className: "adminer-Card__footer", children: footer })
  ] });
}
function CardMeta({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-Card__meta", className), children });
}
var Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Meta: CardMeta
});
var Checkbox = forwardRef(
  function Checkbox2({
    label,
    hint,
    error,
    id: idProp,
    className,
    disabled,
    ...rest
  }, ref) {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: clsx9(
          "adminer-Checkbox",
          disabled && "adminer-Checkbox--disabled",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs("label", { className: "adminer-Checkbox__row", htmlFor: id, children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                ref,
                id,
                type: "checkbox",
                className: "adminer-Checkbox__control",
                disabled,
                ...rest
              }
            ),
            label && /* @__PURE__ */ jsx("span", { className: "adminer-Checkbox__label", children: label })
          ] }),
          error && /* @__PURE__ */ jsx("p", { className: "adminer-Checkbox__error", role: "alert", children: error }),
          !error && hint && /* @__PURE__ */ jsx("p", { className: "adminer-Checkbox__hint", children: hint })
        ]
      }
    );
  }
);
function DescriptionListRoot({ children, className, labelWidth }) {
  const style = labelWidth == null ? void 0 : { "--adminer-description-list-label-width": typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth };
  return /* @__PURE__ */ jsx("dl", { className: clsx9("adminer-DescriptionList", className), style, children });
}
function DescriptionListItem({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "adminer-DescriptionList__item", children: [
    /* @__PURE__ */ jsx("dt", { className: "adminer-DescriptionList__label", children: label }),
    /* @__PURE__ */ jsx("dd", { className: "adminer-DescriptionList__value", children })
  ] });
}
var DescriptionList = Object.assign(DescriptionListRoot, {
  Item: DescriptionListItem
});
var Input = forwardRef(function Input2({
  label,
  error,
  hint,
  suffix,
  id: idProp,
  className,
  ...rest
}, ref) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Input", className), children: [
    label && /* @__PURE__ */ jsx("label", { className: "adminer-Input__label", htmlFor: id, children: label }),
    /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Input__wrap", error && "adminer-Input__wrap--error"), children: [
      /* @__PURE__ */ jsx("input", { ref, id, className: "adminer-Input__control", ...rest }),
      suffix && /* @__PURE__ */ jsx("span", { className: "adminer-Input__suffix", children: suffix })
    ] }),
    error && /* @__PURE__ */ jsx("p", { className: "adminer-Input__error", role: "alert", children: error }),
    !error && hint && /* @__PURE__ */ jsx("p", { className: "adminer-Input__hint", children: hint })
  ] });
});
function PickerBase({
  inputValue,
  onClear,
  renderPicker,
  label,
  error,
  hint,
  id: externalId,
  disabled,
  clearable = true,
  placeholder,
  className,
  dialogLabel,
  clearLabel,
  readOnly = true,
  onInputChange,
  onOpenChange
}) {
  const [open, setOpen] = useState(false);
  const t = useT();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const bp = useBreakpoint();
  const setOpenState = useCallback(
    (next) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const close = useCallback(() => {
    setOpenState(false);
  }, [setOpenState]);
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      const target = e.target;
      if (!rootRef.current?.contains(target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, close]);
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onClear();
    },
    [onClear]
  );
  const isBottomSheet = bp === "sm";
  const openCalendarLabel = t("picker.date.openLabel");
  return /* @__PURE__ */ jsxs("div", { ref: rootRef, className: clsx9("adminer-DatePicker", className), children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        ref: inputRef,
        id: externalId,
        label,
        error,
        hint,
        type: "text",
        value: inputValue,
        placeholder,
        disabled,
        readOnly,
        onChange: open ? onInputChange : void 0,
        onFocus: () => !disabled && setOpenState(true),
        "aria-haspopup": "dialog",
        "aria-expanded": open,
        "aria-invalid": !!error,
        suffix: /* @__PURE__ */ jsxs(Fragment, { children: [
          clearable && inputValue && !disabled && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "adminer-DatePicker__clearBtn",
              onClick: handleClear,
              tabIndex: -1,
              "aria-label": clearLabel,
              children: /* @__PURE__ */ jsx(X, { size: 14 })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "adminer-DatePicker__iconBtn",
              onClick: () => !disabled && setOpenState(!open),
              tabIndex: -1,
              "aria-label": openCalendarLabel,
              disabled,
              children: /* @__PURE__ */ jsx(CalendarDays, { size: 16 })
            }
          )
        ] })
      }
    ),
    open && /* @__PURE__ */ jsxs(Fragment, { children: [
      isBottomSheet && /* @__PURE__ */ jsx("div", { className: "adminer-DatePicker__backdrop", onMouseDown: close }),
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: popoverRef,
          className: clsx9("adminer-DatePicker__popover", isBottomSheet && "adminer-DatePicker__popover--bottomSheet"),
          role: "dialog",
          "aria-modal": "true",
          "aria-label": dialogLabel,
          children: renderPicker(close)
        }
      )
    ] })
  ] });
}

// src/internal/components/DatePicker/pickerUtils.ts
var dayPickerClassNames = {
  months: "adminer-DatePicker__dpMonths",
  month: "adminer-DatePicker__dpMonth",
  month_caption: "adminer-DatePicker__dpCaption",
  caption_label: "adminer-DatePicker__dpCaptionLabel",
  nav: "adminer-DatePicker__dpNav",
  button_previous: "adminer-DatePicker__dpNavBtn",
  button_next: "adminer-DatePicker__dpNavBtn",
  weekdays: "adminer-DatePicker__dpWeekdays",
  weekday: "adminer-DatePicker__dpWeekday",
  weeks: "adminer-DatePicker__dpWeeks",
  week: "adminer-DatePicker__dpWeek",
  week_number_header: "adminer-DatePicker__dpWeekNumberHeader",
  week_number: "adminer-DatePicker__dpWeekNumber",
  day: "adminer-DatePicker__dpDay",
  day_button: "adminer-DatePicker__dpDayBtn",
  selected: "adminer-DatePicker__dpSelected",
  today: "adminer-DatePicker__dpToday",
  outside: "adminer-DatePicker__dpOutside",
  disabled: "adminer-DatePicker__dpDisabled",
  range_start: "adminer-DatePicker__dpRangeStart",
  range_middle: "adminer-DatePicker__dpRangeMiddle",
  range_end: "adminer-DatePicker__dpRangeEnd",
  month_grid: "adminer-DatePicker__dpMonthGrid"
};
function toPragueDay(year, monthIndex, day) {
  const month = String(monthIndex + 1).padStart(2, "0");
  const date = String(day).padStart(2, "0");
  return dayjs.tz(`${year}-${month}-${date}`, "Europe/Prague").startOf("day");
}
function toDayjsDay(day) {
  return toPragueDay(day.getFullYear(), day.getMonth(), day.getDate());
}
function toCalendarDate(value) {
  return new Date(value.year(), value.month(), value.date());
}
function toDateRange(value) {
  if (!value) return void 0;
  return { from: toCalendarDate(value.from), to: toCalendarDate(value.to) };
}
function formatDateRange(value) {
  return value ? `${formatDate(value.from)} \u2013 ${formatDate(value.to)}` : "";
}
function DefaultDatePicker({
  value,
  onChange,
  label,
  error,
  hint,
  id: externalId,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  minDate,
  maxDate,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.date.placeholder");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState(false);
  useEffect(() => {
    if (!open) {
      setDraft(value ? formatDate(value) : "");
      setParseError(false);
    }
  }, [open, value]);
  const handleDaySelect = useCallback(
    (day, close) => {
      if (!day) {
        setDraft("");
        onChange(null);
      } else {
        const next = toPragueDay(day.getFullYear(), day.getMonth(), day.getDate());
        setDraft(formatDate(next));
        onChange(next);
      }
      close();
    },
    [onChange]
  );
  const handleDraftChange = useCallback(
    (e) => {
      const raw = e.target.value;
      setDraft(raw);
      if (!raw) {
        setParseError(false);
        onChange(null);
        return;
      }
      const parsed = dayjs(raw, "DD.MM.YYYY", "cs", true);
      if (parsed.isValid()) {
        setParseError(false);
        onChange(toPragueDay(parsed.year(), parsed.month(), parsed.date()));
      } else {
        setParseError(true);
      }
    },
    [onChange]
  );
  const displayedMonth = value ? toCalendarDate(value) : /* @__PURE__ */ new Date();
  const selectedDate = value ? toCalendarDate(value) : void 0;
  const fromDate = minDate ? toCalendarDate(minDate) : void 0;
  const toDate = maxDate ? toCalendarDate(maxDate) : void 0;
  return /* @__PURE__ */ jsx(
    PickerBase,
    {
      inputValue: open ? draft : value ? formatDate(value) : "",
      onClear: () => {
        onChange(null);
        setDraft("");
      },
      label,
      error: error ?? (parseError ? t("picker.date.invalidFormat") : void 0),
      hint: !parseError ? hint : void 0,
      id: externalId,
      disabled,
      clearable,
      placeholder,
      className,
      dialogLabel: t("picker.date.dialogLabel"),
      clearLabel: t("picker.date.clearLabel"),
      readOnly: !open,
      onInputChange: handleDraftChange,
      onOpenChange: setOpen,
      renderPicker: (close) => /* @__PURE__ */ jsx(
        DayPicker,
        {
          mode: "single",
          selected: selectedDate,
          onSelect: (day) => handleDaySelect(day, close),
          defaultMonth: displayedMonth,
          locale: cs,
          weekStartsOn: 1,
          disabled: [
            ...fromDate ? [{ before: fromDate }] : [],
            ...toDate ? [{ after: toDate }] : []
          ],
          className: "adminer-DatePicker__dayPickerRoot",
          classNames: dayPickerClassNames
        }
      )
    }
  );
}
function DatePicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.DatePicker;
  const Component2 = Override ?? DefaultDatePicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function toWeekRange(day) {
  const selected = toDayjsDay(day);
  return {
    from: selected.startOf("isoWeek"),
    to: selected.endOf("isoWeek").startOf("day")
  };
}
function DefaultWeekPicker({
  value,
  onChange,
  label,
  error,
  hint,
  id,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.week.placeholder");
  const selectedWeek = toDateRange(value);
  const displayedMonth = value ? toCalendarDate(value.from) : /* @__PURE__ */ new Date();
  const selectWeek = useCallback(
    (day, modifiers, close) => {
      if (modifiers.disabled || modifiers.hidden) return;
      onChange(toWeekRange(day));
      close();
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    PickerBase,
    {
      inputValue: formatDateRange(value),
      onClear: () => onChange(null),
      label,
      error,
      hint,
      id,
      disabled,
      clearable,
      placeholder,
      className,
      dialogLabel: t("picker.week.dialogLabel"),
      clearLabel: t("picker.week.clearLabel"),
      renderPicker: (close) => /* @__PURE__ */ jsx(
        DayPicker,
        {
          showOutsideDays: true,
          showWeekNumber: true,
          weekStartsOn: 1,
          defaultMonth: displayedMonth,
          locale: cs,
          className: "adminer-DatePicker__dayPickerRoot adminer-DatePicker__dayPickerRoot--withWeekNumbers",
          modifiers: {
            selected: selectedWeek,
            range_start: selectedWeek?.from,
            range_end: selectedWeek?.to,
            range_middle: (date) => selectedWeek ? rangeIncludesDate(selectedWeek, date, true) : false
          },
          onDayClick: (day, modifiers) => selectWeek(day, modifiers, close),
          onDayKeyDown: (day, modifiers, e) => {
            if (e.key !== " " && e.key !== "Enter") return;
            e.preventDefault();
            selectWeek(day, modifiers, close);
          },
          onWeekNumberClick: (_, week, e) => {
            e.preventDefault();
            onChange({
              from: toDayjsDay(week.from),
              to: toDayjsDay(week.to)
            });
            close();
          },
          classNames: dayPickerClassNames
        }
      )
    }
  );
}
function WeekPicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.WeekPicker;
  const Component2 = Override ?? DefaultWeekPicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function toMonthRange(day) {
  const selected = toDayjsDay(day);
  return {
    from: selected.startOf("month"),
    to: selected.endOf("month").startOf("day")
  };
}
function DefaultMonthPicker({
  value,
  onChange,
  label,
  error,
  hint,
  id,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.month.placeholder");
  const selectedMonth = toDateRange(value);
  const displayedMonth = value ? toCalendarDate(value.from) : /* @__PURE__ */ new Date();
  const selectMonth = useCallback(
    (day, modifiers, close) => {
      if (modifiers.disabled || modifiers.hidden) return;
      onChange(toMonthRange(day));
      close();
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    PickerBase,
    {
      inputValue: value ? value.from.format("MMMM YYYY") : "",
      onClear: () => onChange(null),
      label,
      error,
      hint,
      id,
      disabled,
      clearable,
      placeholder,
      className,
      dialogLabel: t("picker.month.dialogLabel"),
      clearLabel: t("picker.month.clearLabel"),
      renderPicker: (close) => /* @__PURE__ */ jsx(
        DayPicker,
        {
          showOutsideDays: true,
          weekStartsOn: 1,
          defaultMonth: displayedMonth,
          locale: cs,
          className: "adminer-DatePicker__dayPickerRoot",
          modifiers: {
            selected: selectedMonth,
            range_start: selectedMonth?.from,
            range_end: selectedMonth?.to,
            range_middle: (date) => selectedMonth ? rangeIncludesDate(selectedMonth, date, true) : false
          },
          onDayClick: (day, modifiers) => selectMonth(day, modifiers, close),
          onDayKeyDown: (day, modifiers, e) => {
            if (e.key !== " " && e.key !== "Enter") return;
            e.preventDefault();
            selectMonth(day, modifiers, close);
          },
          classNames: dayPickerClassNames
        }
      )
    }
  );
}
function MonthPicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.MonthPicker;
  const Component2 = Override ?? DefaultMonthPicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function DefaultDateRangePicker({
  value,
  onChange,
  label,
  error,
  hint,
  id,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.range.placeholder");
  const [draftRange, setDraftRange] = useState(
    () => toDateRange(value)
  );
  useEffect(() => {
    setDraftRange(toDateRange(value));
  }, [value]);
  const displayedMonth = value ? toCalendarDate(value.from) : /* @__PURE__ */ new Date();
  return /* @__PURE__ */ jsx(
    PickerBase,
    {
      inputValue: formatDateRange(value),
      onClear: () => {
        setDraftRange(void 0);
        onChange(null);
      },
      label,
      error,
      hint,
      id,
      disabled,
      clearable,
      placeholder,
      className,
      dialogLabel: t("picker.range.dialogLabel"),
      clearLabel: t("picker.range.clearLabel"),
      renderPicker: (close) => /* @__PURE__ */ jsx(
        DayPicker,
        {
          mode: "range",
          resetOnSelect: true,
          selected: draftRange,
          onSelect: (next) => {
            setDraftRange(next);
            if (!next) {
              onChange(null);
              return;
            }
            if (next.from && next.to) {
              onChange({
                from: toDayjsDay(next.from),
                to: toDayjsDay(next.to)
              });
              close();
            }
          },
          defaultMonth: displayedMonth,
          locale: cs,
          weekStartsOn: 1,
          className: "adminer-DatePicker__dayPickerRoot",
          classNames: dayPickerClassNames
        }
      )
    }
  );
}
function DateRangePicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.DateRangePicker;
  const Component2 = Override ?? DefaultDateRangePicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function DefaultDateTimePicker({
  value,
  onChange,
  label,
  error,
  hint,
  id: externalId,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  minDate,
  maxDate,
  minuteStep = 1,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.datetime.placeholder");
  const autoId = useId();
  const id = externalId ?? autoId;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const bp = useBreakpoint();
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      const target = e.target;
      if (!popoverRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, close]);
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);
  const handleDaySelect = useCallback(
    (day) => {
      if (!day) {
        onChange(null);
        return;
      }
      const base = value ?? dayjs().tz("Europe/Prague").startOf("day");
      onChange(
        base.year(day.getFullYear()).month(day.getMonth()).date(day.getDate())
      );
    },
    [value, onChange]
  );
  const handleHourClick = useCallback(
    (h) => {
      const base = value ?? dayjs().tz("Europe/Prague").startOf("day");
      onChange(base.hour(h).second(0).millisecond(0));
    },
    [value, onChange]
  );
  const handleMinuteClick = useCallback(
    (m) => {
      const base = value ?? dayjs().tz("Europe/Prague").startOf("day");
      onChange(base.minute(m).second(0).millisecond(0));
    },
    [value, onChange]
  );
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
    },
    [onChange]
  );
  const selectedDate = value?.toDate();
  const selectedHour = value?.hour() ?? null;
  const selectedMinute = value?.minute() ?? null;
  const displayedMonth = value ? value.toDate() : /* @__PURE__ */ new Date();
  const fromDate = minDate?.toDate();
  const toDate = maxDate?.toDate();
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => i * minuteStep
  ).filter((m) => m < 60);
  const isBottomSheet = bp === "sm";
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-DateTimePicker", className), children: [
    label && /* @__PURE__ */ jsx("label", { className: "adminer-DateTimePicker__label", htmlFor: id, children: label }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: clsx9(
          "adminer-DateTimePicker__inputWrap",
          error && "adminer-DateTimePicker__inputWrap--error",
          open && "adminer-DateTimePicker__inputWrap--focused",
          disabled && "adminer-DateTimePicker__inputWrap--disabled"
        ),
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              id,
              className: "adminer-DateTimePicker__input",
              type: "text",
              value: value ? formatDateTime(value) : "",
              placeholder,
              disabled,
              readOnly: true,
              onFocus: () => !disabled && setOpen(true),
              "aria-haspopup": "dialog",
              "aria-expanded": open,
              "aria-invalid": !!error
            }
          ),
          clearable && value && !disabled && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "adminer-DateTimePicker__clearBtn",
              onClick: handleClear,
              tabIndex: -1,
              "aria-label": t("picker.datetime.clearLabel"),
              children: /* @__PURE__ */ jsx(X, { size: 14 })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              ref: triggerRef,
              type: "button",
              className: "adminer-DateTimePicker__iconBtn",
              onClick: () => !disabled && setOpen((v) => !v),
              tabIndex: -1,
              "aria-label": t("picker.datetime.openLabel"),
              disabled,
              children: /* @__PURE__ */ jsx(CalendarDays, { size: 16 })
            }
          )
        ]
      }
    ),
    error && /* @__PURE__ */ jsx("p", { className: "adminer-DateTimePicker__error", role: "alert", children: error }),
    !error && hint && /* @__PURE__ */ jsx("p", { className: "adminer-DateTimePicker__hint", children: hint }),
    open && /* @__PURE__ */ jsxs(Fragment, { children: [
      isBottomSheet && /* @__PURE__ */ jsx("div", { className: "adminer-DateTimePicker__backdrop", onMouseDown: close }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          ref: popoverRef,
          className: clsx9("adminer-DateTimePicker__popover", isBottomSheet && "adminer-DateTimePicker__popover--bottomSheet"),
          role: "dialog",
          "aria-modal": "true",
          "aria-label": t("picker.datetime.dialogLabel"),
          children: [
            /* @__PURE__ */ jsx(
              DayPicker,
              {
                mode: "single",
                selected: selectedDate,
                onSelect: handleDaySelect,
                defaultMonth: displayedMonth,
                locale: cs,
                weekStartsOn: 1,
                disabled: [
                  ...fromDate ? [{ before: fromDate }] : [],
                  ...toDate ? [{ after: toDate }] : []
                ],
                className: "adminer-DatePicker__dayPickerRoot",
                classNames: dayPickerClassNames
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "adminer-DateTimePicker__timeSeparator" }),
            /* @__PURE__ */ jsx("div", { className: "adminer-DateTimePicker__timeSection", children: /* @__PURE__ */ jsxs("div", { className: "adminer-DateTimePicker__timeCols", children: [
              /* @__PURE__ */ jsxs("div", { className: "adminer-DateTimePicker__timeColWrap", children: [
                /* @__PURE__ */ jsx("span", { className: "adminer-DateTimePicker__timeColLabel", children: t("picker.time.hours") }),
                /* @__PURE__ */ jsx("ul", { className: "adminer-DateTimePicker__timeCol", role: "listbox", "aria-label": t("picker.time.hours"), children: hours.map((h) => /* @__PURE__ */ jsx(
                  "li",
                  {
                    role: "option",
                    "aria-selected": h === selectedHour,
                    className: clsx9(
                      "adminer-DateTimePicker__timeOpt",
                      h === selectedHour && "adminer-DateTimePicker__timeOpt--selected"
                    ),
                    onClick: () => handleHourClick(h),
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") handleHourClick(h);
                    },
                    tabIndex: 0,
                    children: String(h).padStart(2, "0")
                  },
                  h
                )) })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: "adminer-DateTimePicker__timeColWrap", children: [
                /* @__PURE__ */ jsx("span", { className: "adminer-DateTimePicker__timeColLabel", children: t("picker.time.minutes") }),
                /* @__PURE__ */ jsx("ul", { className: "adminer-DateTimePicker__timeCol", role: "listbox", "aria-label": t("picker.time.minutes"), children: minutes.map((m) => /* @__PURE__ */ jsx(
                  "li",
                  {
                    role: "option",
                    "aria-selected": m === selectedMinute,
                    className: clsx9(
                      "adminer-DateTimePicker__timeOpt",
                      m === selectedMinute && "adminer-DateTimePicker__timeOpt--selected"
                    ),
                    onClick: () => handleMinuteClick(m),
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") handleMinuteClick(m);
                    },
                    tabIndex: 0,
                    children: String(m).padStart(2, "0")
                  },
                  m
                )) })
              ] })
            ] }) }),
            /* @__PURE__ */ jsx("div", { className: "adminer-DateTimePicker__footer", children: /* @__PURE__ */ jsx("button", { type: "button", className: "adminer-DateTimePicker__confirmBtn", onClick: close, children: t("common.confirm") }) })
          ]
        }
      )
    ] })
  ] });
}
function DateTimePicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.DateTimePicker;
  const Component2 = Override ?? DefaultDateTimePicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
var DefaultInbox = forwardRef(function DefaultInbox2({ size = 24, strokeWidth = 2, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      ...props,
      ref,
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx("polyline", { points: "22 12 16 12 14 15 10 15 8 12 2 12" }),
        /* @__PURE__ */ jsx("path", { d: "M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" })
      ]
    }
  );
});
function EmptyState({
  icon: Icon = DefaultInbox,
  title,
  description,
  action
}) {
  return /* @__PURE__ */ jsxs("div", { className: "adminer-EmptyState", children: [
    /* @__PURE__ */ jsx(Icon, { className: "adminer-EmptyState__icon", size: 40, strokeWidth: 1.5, "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("p", { className: "adminer-EmptyState__title", children: title }),
    description && /* @__PURE__ */ jsx("p", { className: "adminer-EmptyState__description", children: description }),
    action && /* @__PURE__ */ jsx("div", { className: "adminer-EmptyState__action", children: action })
  ] });
}
function ErrorBoundaryFallback({
  onReload,
  onGoHome
}) {
  const t = useT();
  return /* @__PURE__ */ jsx("main", { className: "adminer-ErrorBoundary", children: /* @__PURE__ */ jsxs(
    Card,
    {
      className: "adminer-ErrorBoundary__card",
      header: /* @__PURE__ */ jsx(
        Card.Header,
        {
          title: t("errorBoundary.title"),
          subtitle: t("errorBoundary.subtitle")
        }
      ),
      children: [
        /* @__PURE__ */ jsx(Alert, { variant: "err", title: t("errorBoundary.alertTitle"), children: t("errorBoundary.alertBody") }),
        /* @__PURE__ */ jsxs("div", { className: "adminer-ErrorBoundary__actions", children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "default",
              onClick: onGoHome,
              className: "adminer-ErrorBoundary__actionButton",
              children: t("errorBoundary.actionHome")
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "primary",
              onClick: onReload,
              className: "adminer-ErrorBoundary__actionButton",
              children: t("errorBoundary.actionReload")
            }
          )
        ] })
      ]
    }
  ) });
}
function reloadPage() {
  window.location.reload();
}
function goHome() {
  window.location.assign("/");
}
var ErrorBoundary = class extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Unhandled React error", error, errorInfo);
  }
  handleReload = () => {
    (this.props.onReload ?? reloadPage)();
  };
  handleGoHome = () => {
    (this.props.onGoHome ?? goHome)();
  };
  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    return /* @__PURE__ */ jsx(
      ErrorBoundaryFallback,
      {
        onReload: this.handleReload,
        onGoHome: this.handleGoHome
      }
    );
  }
};
function getMessage(error) {
  if (!error) return void 0;
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return void 0;
}
function ErrorState({
  title = "Something went wrong",
  error,
  onRetry,
  retryLabel = "Try again"
}) {
  const message = getMessage(error);
  return /* @__PURE__ */ jsxs("div", { className: "adminer-ErrorState", children: [
    /* @__PURE__ */ jsx(Alert, { variant: "err", title, children: message }),
    onRetry && /* @__PURE__ */ jsx("div", { className: "adminer-ErrorState__retry", children: /* @__PURE__ */ jsx(Button, { variant: "default", size: "sm", onClick: onRetry, children: retryLabel }) })
  ] });
}

// src/internal/components/Field/getValueAtPath.ts
function getValueAtPath(obj, path) {
  if (obj == null) return void 0;
  const parts = path.split(".");
  let cur = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return void 0;
    cur = cur[part];
  }
  return cur;
}
function isEmpty(value) {
  return value === null || value === void 0 || value === "";
}
function wrap(content, className) {
  if (!className) return content;
  return /* @__PURE__ */ jsx("span", { className, children: content });
}
function Field({
  record,
  source,
  type = "text",
  render,
  options,
  emptyText,
  className
}) {
  const t = useT();
  const value = record == null ? void 0 : getValueAtPath(record, source);
  if (render && record != null) {
    return wrap(render(value, record), className);
  }
  const placeholder = emptyText ?? getFormattingConfig().placeholder;
  if (isEmpty(value)) return wrap(placeholder, className);
  switch (type) {
    case "date":
      return wrap(formatDate(value), className);
    case "datetime":
      return wrap(formatDateTime(value), className);
    case "time":
      return wrap(formatTime(value), className);
    case "weekday":
      return wrap(formatWeekday(value), className);
    case "relative":
      return wrap(formatRelative(value), className);
    case "currency": {
      const currency = options?.currency ?? void 0;
      const decimals = options?.decimals;
      return wrap(formatCurrency(value, { currency, decimals }), className);
    }
    case "number":
      return wrap(formatNumber(value), className);
    case "boolean":
      return wrap(value ? t("field.boolean.true") : t("field.boolean.false"), className);
    case "email":
      return wrap(
        /* @__PURE__ */ jsx("a", { href: `mailto:${String(value)}`, className: "adminer-Field__email", children: String(value) }),
        className
      );
    case "text":
    default:
      return wrap(String(value), className);
  }
}
function TextField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "text" });
}
function DateField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "date" });
}
function DateTimeField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "datetime" });
}
function TimeField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "time" });
}
function WeekdayField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "weekday" });
}
function RelativeDateField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "relative" });
}
function NumberField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "number" });
}
function CurrencyField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "currency" });
}
function BooleanField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "boolean" });
}
function EmailField(props) {
  return /* @__PURE__ */ jsx(Field, { ...props, type: "email" });
}
function FilterPanel({
  children,
  className,
  formProps
}) {
  const row = /* @__PURE__ */ jsx("div", { className: "adminer-FilterPanel__row", children });
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-FilterPanel", className), children: formProps ? /* @__PURE__ */ jsx("form", { ...formProps, children: row }) : row });
}
function FilterField({
  children,
  wide = false,
  action = false,
  className,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx9(
        "adminer-FilterField",
        wide && "adminer-FilterField--wide",
        action && "adminer-FilterField--action",
        className
      ),
      ...rest,
      children
    }
  );
}
function FormStack({ children, className, as: Component2 = "div", ...rest }) {
  return /* @__PURE__ */ jsx(Component2, { className: clsx9("adminer-FormStack", className), ...rest, children });
}
function FormSectionLabel({ children, className, ...rest }) {
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-FormStack__sectionLabel", className), ...rest, children });
}
function Spinner({ size = "md", className, ariaLabel = "Loading\u2026" }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: clsx9("adminer-Spinner", `adminer-Spinner--${size}`, className),
      "aria-label": ariaLabel,
      role: "status"
    }
  );
}
function InlineLoading({ children, className, mono = false }) {
  return /* @__PURE__ */ jsxs("span", { className: clsx9("adminer-InlineLoading", mono && "adminer-InlineLoading--mono", className), children: [
    /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
    children
  ] });
}
function KPI({ label, value, delta, deltaDirection, hi, className }) {
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-KPI", hi && "adminer-KPI--hi", className), children: [
    /* @__PURE__ */ jsx("div", { className: "adminer-KPI__label", children: label }),
    /* @__PURE__ */ jsx("div", { className: "adminer-KPI__value", children: value }),
    delta !== void 0 && /* @__PURE__ */ jsx("div", { className: clsx9("adminer-KPI__delta", deltaDirection && `adminer-KPI__delta--${deltaDirection}`), children: delta })
  ] });
}
function KPIGrid({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-KPIGrid", className), children });
}
function LoadingState({ label = "Loading\u2026" }) {
  return /* @__PURE__ */ jsxs("div", { className: "adminer-LoadingState", role: "status", "aria-label": label, children: [
    /* @__PURE__ */ jsx(Spinner, { size: "md" }),
    /* @__PURE__ */ jsx("span", { className: "adminer-LoadingState__label", children: label })
  ] });
}
function DefaultModal({
  open,
  title,
  onClose,
  children,
  footer,
  width,
  closeLabel = "Close"
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "adminer-Modal__backdrop",
        onMouseDown: (event) => {
          if (event.target === event.currentTarget) onClose();
        },
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            ref: dialogRef,
            className: "adminer-Modal",
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": titleId,
            style: width !== void 0 ? { width: typeof width === "number" ? `${width}px` : width } : void 0,
            children: [
              /* @__PURE__ */ jsxs("div", { className: "adminer-Modal__header", children: [
                /* @__PURE__ */ jsx("h2", { id: titleId, className: "adminer-Modal__title", children: title }),
                /* @__PURE__ */ jsx("button", { className: "adminer-Modal__close", onClick: onClose, "aria-label": closeLabel, type: "button", children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xD7" }) })
              ] }),
              /* @__PURE__ */ jsx("div", { className: "adminer-Modal__body", children }),
              footer && /* @__PURE__ */ jsx("div", { className: "adminer-Modal__footer", children: footer })
            ]
          }
        )
      }
    ),
    document.body
  );
}
function Modal(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.Modal;
  const Component2 = Override ?? DefaultModal;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function PageContent({ children, className, stack = false, gap = "md" }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx9(
        "adminer-PageContent",
        stack && "adminer-PageContent--stack",
        stack && gap !== "md" && `adminer-PageContent--gap${gap === "sm" ? "Sm" : "Lg"}`,
        className
      ),
      children
    }
  );
}
function isRenderable2(node) {
  return node !== null && node !== void 0 && node !== false;
}
function decorateSubtitle(subtitle, variant) {
  if (typeof subtitle !== "string") return subtitle;
  if (variant === "meta") return subtitle;
  const trimmed = subtitle.trimStart();
  return trimmed.startsWith("//") ? subtitle : `// ${subtitle}`;
}
function PageHeader({
  title,
  subtitle,
  subtitleVariant = "tagline",
  actions,
  titleId,
  className
}) {
  const decoratedSubtitle = isRenderable2(subtitle) ? decorateSubtitle(subtitle, subtitleVariant) : null;
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-PageHeader", className), "data-page-header": true, children: [
    /* @__PURE__ */ jsxs("div", { className: "adminer-PageHeader__titleBlock", children: [
      /* @__PURE__ */ jsx("h1", { id: titleId, className: "adminer-PageHeader__title", children: title }),
      isRenderable2(decoratedSubtitle) && /* @__PURE__ */ jsx(
        "div",
        {
          className: clsx9(
            "adminer-PageHeader__subtitle",
            subtitleVariant === "tagline" && "adminer-PageHeader__subtitle--tagline"
          ),
          children: decoratedSubtitle
        }
      )
    ] }),
    isRenderable2(actions) && /* @__PURE__ */ jsx("div", { className: "adminer-PageHeader__actions", children: actions })
  ] });
}
function isRenderable3(node) {
  return node !== null && node !== void 0 && node !== false;
}
function SectionHeader({ title, meta, titleId, className }) {
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-SectionHeader", className), children: [
    /* @__PURE__ */ jsx("h2", { id: titleId, className: "adminer-SectionHeader__title", children: title }),
    /* @__PURE__ */ jsx("div", { className: "adminer-SectionHeader__rule", "aria-hidden": "true" }),
    isRenderable3(meta) && /* @__PURE__ */ jsx("div", { className: "adminer-SectionHeader__meta", children: meta })
  ] });
}

// src/internal/components/Page/Page.tsx
var Page = {
  Content: PageContent,
  Header: PageHeader,
  SectionHeader
};
function Pagination({
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  summary,
  limit,
  limitOptions,
  onLimitChange,
  prevLabel = "Previous",
  nextLabel = "Next",
  prevIcon,
  nextIcon,
  limitLabel = "per page",
  limitAriaLabel = "Items per page"
}) {
  const hasLimitSelector = Boolean(limitOptions?.length && onLimitChange);
  if (!hasPrev && !hasNext && !hasLimitSelector) return null;
  return /* @__PURE__ */ jsxs("div", { className: "adminer-Pagination", children: [
    hasLimitSelector && /* @__PURE__ */ jsxs("div", { className: "adminer-Pagination__limitWrapper", children: [
      /* @__PURE__ */ jsx(
        "select",
        {
          className: "adminer-Pagination__limitSelect",
          value: limit,
          onChange: (event) => onLimitChange?.(Number(event.target.value)),
          "aria-label": limitAriaLabel,
          children: limitOptions?.map((n) => /* @__PURE__ */ jsx("option", { value: n, children: n }, n))
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "adminer-Pagination__limitLabel", children: limitLabel })
    ] }),
    summary && /* @__PURE__ */ jsx("span", { className: "adminer-Pagination__summary", children: summary }),
    /* @__PURE__ */ jsxs(Button, { variant: "default", onClick: onPrev, disabled: !hasPrev, children: [
      prevIcon,
      prevLabel
    ] }),
    /* @__PURE__ */ jsxs(Button, { variant: "default", onClick: onNext, disabled: !hasNext, children: [
      nextLabel,
      nextIcon
    ] })
  ] });
}
function ResponsiveGrid({
  children,
  columns = 2,
  gap = "md",
  className
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx9(
        "adminer-ResponsiveGrid",
        `adminer-ResponsiveGrid--cols${columns}`,
        gap !== "md" && `adminer-ResponsiveGrid--gap${gap === "sm" ? "Sm" : "Lg"}`,
        className
      ),
      children
    }
  );
}
function Select({
  label,
  error,
  hint,
  id: idProp,
  className,
  children,
  ...rest
}) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Select", className), children: [
    label && /* @__PURE__ */ jsx("label", { className: "adminer-Select__label", htmlFor: id, children: label }),
    /* @__PURE__ */ jsx(
      "select",
      {
        id,
        className: clsx9("adminer-Select__control", error && "adminer-Select__control--error"),
        ...rest,
        children
      }
    ),
    error && /* @__PURE__ */ jsx("p", { className: "adminer-Select__error", role: "alert", children: error }),
    !error && hint && /* @__PURE__ */ jsx("p", { className: "adminer-Select__hint", children: hint })
  ] });
}
function Stat({ label, value, delta, deltaDirection, className }) {
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Stat", className), children: [
    /* @__PURE__ */ jsx("div", { className: "adminer-Stat__label", children: label }),
    /* @__PURE__ */ jsx("div", { className: "adminer-Stat__value", children: value }),
    delta !== void 0 && /* @__PURE__ */ jsx("div", { className: clsx9("adminer-Stat__delta", deltaDirection && `adminer-Stat__delta--${deltaDirection}`), children: delta })
  ] });
}
function DefaultTimePicker({
  value,
  onChange,
  label,
  error,
  hint,
  id: externalId,
  disabled,
  clearable = true,
  placeholder: placeholderProp,
  minuteStep = 1,
  className
}) {
  const t = useT();
  const placeholder = placeholderProp ?? t("picker.time.placeholder");
  const autoId = useId();
  const id = externalId ?? autoId;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);
  const bp = useBreakpoint();
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => i * minuteStep
  ).filter((m) => m < 60);
  const selectedHour = value?.hour() ?? null;
  const selectedMinute = value?.minute() ?? null;
  useEffect(() => {
    if (!open) {
      setDraft(value ? formatTime(value) : "");
      setParseError(false);
    }
  }, [open, value]);
  useEffect(() => {
    if (!open) return;
    const scrollSelected = (listRef) => {
      const selected = listRef.current?.querySelector('[aria-selected="true"]');
      selected?.scrollIntoView({ block: "nearest" });
    };
    const timer = setTimeout(() => {
      scrollSelected(hourListRef);
      scrollSelected(minuteListRef);
    }, 10);
    return () => clearTimeout(timer);
  }, [open]);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      const target = e.target;
      if (!popoverRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open, close]);
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);
  const setTime = useCallback(
    (hour, minute) => {
      const base = value ?? dayjs().tz("Europe/Prague");
      onChange(base.hour(hour).minute(minute).second(0).millisecond(0));
    },
    [value, onChange]
  );
  const handleHourClick = useCallback(
    (h) => setTime(h, selectedMinute ?? 0),
    [setTime, selectedMinute]
  );
  const handleMinuteClick = useCallback(
    (m) => setTime(selectedHour ?? 0, m),
    [setTime, selectedHour]
  );
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
      setDraft("");
    },
    [onChange]
  );
  const handleDraftChange = useCallback(
    (e) => {
      const raw = e.target.value;
      setDraft(raw);
      if (!raw) {
        setParseError(false);
        onChange(null);
        return;
      }
      const parsed = dayjs(raw, "HH:mm", "cs", true);
      if (parsed.isValid()) {
        setParseError(false);
        const base = value ?? dayjs().tz("Europe/Prague");
        onChange(base.hour(parsed.hour()).minute(parsed.minute()).second(0).millisecond(0));
      } else {
        setParseError(true);
      }
    },
    [value, onChange]
  );
  const isBottomSheet = bp === "sm";
  return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-TimePicker", className), children: [
    label && /* @__PURE__ */ jsx("label", { className: "adminer-TimePicker__label", htmlFor: id, children: label }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: clsx9(
          "adminer-TimePicker__inputWrap",
          (error || parseError) && "adminer-TimePicker__inputWrap--error",
          open && "adminer-TimePicker__inputWrap--focused",
          disabled && "adminer-TimePicker__inputWrap--disabled"
        ),
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              id,
              className: "adminer-TimePicker__input",
              type: "text",
              value: open ? draft : value ? formatTime(value) : "",
              placeholder,
              disabled,
              readOnly: !open,
              onChange: open ? handleDraftChange : void 0,
              onFocus: () => !disabled && setOpen(true),
              "aria-haspopup": "dialog",
              "aria-expanded": open,
              "aria-invalid": !!(error || parseError)
            }
          ),
          clearable && value && !disabled && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "adminer-TimePicker__clearBtn",
              onClick: handleClear,
              tabIndex: -1,
              "aria-label": t("picker.time.clearLabel"),
              children: /* @__PURE__ */ jsx(X, { size: 14 })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              ref: triggerRef,
              type: "button",
              className: "adminer-TimePicker__iconBtn",
              onClick: () => !disabled && setOpen((v) => !v),
              tabIndex: -1,
              "aria-label": t("picker.time.openLabel"),
              disabled,
              children: /* @__PURE__ */ jsx(Clock, { size: 16 })
            }
          )
        ]
      }
    ),
    (error || parseError) && /* @__PURE__ */ jsx("p", { className: "adminer-TimePicker__error", role: "alert", children: error ?? t("picker.time.invalidFormat") }),
    !error && !parseError && hint && /* @__PURE__ */ jsx("p", { className: "adminer-TimePicker__hint", children: hint }),
    open && /* @__PURE__ */ jsxs(Fragment, { children: [
      isBottomSheet && /* @__PURE__ */ jsx("div", { className: "adminer-TimePicker__backdrop", onMouseDown: close }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          ref: popoverRef,
          className: clsx9("adminer-TimePicker__popover", isBottomSheet && "adminer-TimePicker__popover--bottomSheet"),
          role: "dialog",
          "aria-modal": "true",
          "aria-label": t("picker.time.dialogLabel"),
          children: [
            /* @__PURE__ */ jsxs("div", { className: "adminer-TimePicker__columnsHeader", children: [
              /* @__PURE__ */ jsx("span", { children: t("picker.time.hours") }),
              /* @__PURE__ */ jsx("span", { children: t("picker.time.minutes") })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "adminer-TimePicker__columns", children: [
              /* @__PURE__ */ jsx(
                "ul",
                {
                  ref: hourListRef,
                  className: "adminer-TimePicker__column",
                  role: "listbox",
                  "aria-label": t("picker.time.hours"),
                  children: hours.map((h) => /* @__PURE__ */ jsx(
                    "li",
                    {
                      role: "option",
                      "aria-selected": h === selectedHour,
                      className: clsx9(
                        "adminer-TimePicker__option",
                        h === selectedHour && "adminer-TimePicker__option--selected"
                      ),
                      onClick: () => handleHourClick(h),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") handleHourClick(h);
                      },
                      tabIndex: 0,
                      children: String(h).padStart(2, "0")
                    },
                    h
                  ))
                }
              ),
              /* @__PURE__ */ jsx(
                "ul",
                {
                  ref: minuteListRef,
                  className: "adminer-TimePicker__column",
                  role: "listbox",
                  "aria-label": t("picker.time.minutes"),
                  children: minutes.map((m) => /* @__PURE__ */ jsx(
                    "li",
                    {
                      role: "option",
                      "aria-selected": m === selectedMinute,
                      className: clsx9(
                        "adminer-TimePicker__option",
                        m === selectedMinute && "adminer-TimePicker__option--selected"
                      ),
                      onClick: () => handleMinuteClick(m),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") handleMinuteClick(m);
                      },
                      tabIndex: 0,
                      children: String(m).padStart(2, "0")
                    },
                    m
                  ))
                }
              )
            ] }),
            /* @__PURE__ */ jsx("div", { className: "adminer-TimePicker__footer", children: /* @__PURE__ */ jsx("button", { type: "button", className: "adminer-TimePicker__confirmBtn", onClick: close, children: t("common.confirm") }) })
          ]
        }
      )
    ] })
  ] });
}
function TimePicker(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.TimePicker;
  const Component2 = Override ?? DefaultTimePicker;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
var VARIANT_CLASS = {
  default: "",
  primary: "adminer-TableAction--primary",
  danger: "adminer-TableAction--danger"
};
function TableActions({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-TableActions", className), children });
}
function TableAction(props) {
  const { children, className, variant = "default", iconOnly = false } = props;
  const ctx = useContext(AdminerContext);
  const Link = ctx?.LinkComponent ?? defaultLinkComponent;
  const actionClassName = clsx9(
    "adminer-TableAction",
    VARIANT_CLASS[variant],
    iconOnly && "adminer-TableAction--iconOnly",
    className
  );
  if (props.to !== void 0) {
    const { to, linkProps } = props;
    return /* @__PURE__ */ jsx(Link, { to, className: actionClassName, ...linkProps ?? {}, children });
  }
  const {
    type = "button",
    children: _c,
    className: _cn,
    variant: _v,
    iconOnly: _i,
    to: _t,
    ...buttonProps
  } = props;
  return /* @__PURE__ */ jsx("button", { ...buttonProps, type, className: actionClassName, children });
}
var STATUS_CLASS = {
  ok: "adminer-Table__row--ok",
  warn: "adminer-Table__row--warn",
  err: "adminer-Table__row--err"
};
function TableRoot({
  columns,
  rows,
  rowKey,
  rowStatus,
  className,
  empty
}) {
  return /* @__PURE__ */ jsx("div", { className: clsx9("adminer-Table__wrap", className), children: /* @__PURE__ */ jsxs("table", { className: "adminer-Table", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { children: columns.map((col) => /* @__PURE__ */ jsx("th", { children: col.header }, col.key)) }) }),
    /* @__PURE__ */ jsx("tbody", { children: rows.length === 0 && empty ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(
      "td",
      {
        colSpan: columns.length,
        className: "adminer-Table__emptyCell",
        children: empty
      }
    ) }) : rows.map((row) => {
      const status = rowStatus?.(row);
      return /* @__PURE__ */ jsx(
        "tr",
        {
          className: status ? STATUS_CLASS[status] : void 0,
          children: columns.map((col) => /* @__PURE__ */ jsx("td", { className: col.tdClass, children: col.render(row) }, col.key))
        },
        rowKey(row)
      );
    }) })
  ] }) });
}
var Table = Object.assign(TableRoot, {
  Actions: TableActions,
  Action: TableAction
});
function DefaultTabs({ tabs, value, onChange, "aria-label": ariaLabel }) {
  return /* @__PURE__ */ jsx("div", { className: "adminer-Tabs", role: "tablist", "aria-label": ariaLabel, children: tabs.map((tab) => /* @__PURE__ */ jsx(
    "button",
    {
      role: "tab",
      "aria-selected": value === tab.id,
      "aria-disabled": tab.disabled,
      disabled: tab.disabled,
      className: "adminer-Tabs__tab",
      "data-active": value === tab.id ? "true" : void 0,
      onClick: () => !tab.disabled && onChange(tab.id),
      type: "button",
      children: tab.label
    },
    tab.id
  )) });
}
function Tabs(props) {
  const ctx = useContext(AdminerContext);
  const Override = ctx?.components.Tabs;
  const Component2 = Override ?? DefaultTabs;
  return /* @__PURE__ */ jsx(Component2, { ...props });
}
function Text({ as: Component2 = "span", variant, className, children, ...rest }) {
  const variants = Array.isArray(variant) ? variant : variant ? [variant] : [];
  return /* @__PURE__ */ jsx(Component2, { className: clsx9("adminer-Text", variants.map((v) => `adminer-Text--${v}`), className), ...rest, children });
}
var Textarea = forwardRef(
  function Textarea2({
    label,
    error,
    hint,
    id: idProp,
    className,
    rows = 4,
    ...rest
  }, ref) {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    return /* @__PURE__ */ jsxs("div", { className: clsx9("adminer-Textarea", className), children: [
      label && /* @__PURE__ */ jsx("label", { className: "adminer-Textarea__label", htmlFor: id, children: label }),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: clsx9(
            "adminer-Textarea__wrap",
            error && "adminer-Textarea__wrap--error"
          ),
          children: /* @__PURE__ */ jsx(
            "textarea",
            {
              ref,
              id,
              rows,
              className: "adminer-Textarea__control",
              ...rest
            }
          )
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "adminer-Textarea__error", role: "alert", children: error }),
      !error && hint && /* @__PURE__ */ jsx("p", { className: "adminer-Textarea__hint", children: hint })
    ] });
  }
);

export { Alert, Badge, BooleanField, Button, Card, CardHeader, Checkbox, CurrencyField, DateField, DatePicker, DateRangePicker, DateTimeField, DateTimePicker, DescriptionList, EmailField, EmptyState, ErrorBoundary, ErrorBoundaryFallback, ErrorState, Field, FilterField, FilterPanel, FormSectionLabel, FormStack, InlineLoading, Input, KPI, KPIGrid, LoadingState, Modal, MonthPicker, NumberField, Page, PageContent, PageHeader, Pagination, RelativeDateField, ResponsiveGrid, SectionHeader, Select, Spinner, Stat, Table, TableAction, TableActions, Tabs, Text, TextField, Textarea, TimeField, TimePicker, WeekPicker, WeekdayField, getValueAtPath };
//# sourceMappingURL=components.js.map
//# sourceMappingURL=components.js.map